import AIcon from '@/components/A/AIcon/AIcon'
import styles from './ProjectCard.module.less'
import { PAGE_LINKS } from '@/router/pageConst'
import AImage from '@/components/A/AImage/AImage'
import { Dropdown, message, Popover } from 'antd'
import dayjs from 'dayjs'
import React, { useContext, useState } from 'react'
import { useLocation } from 'react-router-dom'
import ProgressLine from '../ProgressLine/ProgressLine'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { commonUtils } from '@/helpers/commonUtils'
import { ProgressContext, ProjectPageContext } from '../ProjectPageContent/ProjectPageContent'
import { dayjsService } from '@/services/dayjsService'
import { INTERNAL_HOST } from '@/commonConst'
import useNewNavigate from '@/helpers/commonHooks/useNewNavigate'

interface ProjectProps {
  item: ProjectModels.ProjectListItem & {
    // 专家分析状态
    expertAnalyseStatus?: 'part' | 'completed' | 'untreated'
  }
  mode?: 'default' | 'professor'
  prioritizeCover?: boolean
  isPortrait?: boolean
}

const ProjectCard = ({
  item,
  mode = 'default',
  prioritizeCover = false,
  isPortrait = false,
}: ProjectProps) => {
  const { t } = useTranslation()
  const { progressData } = useContext(ProgressContext)
  const navigate = useNewNavigate()
  const routerLocation = useLocation()
  const context = useContext(ProjectPageContext)
  const [moreBtnOpen, setMoreBtnOpen] = useState(false)
  const menuItems = [
    {
      key: 'reanalyze',
      label: (
        <span
          data-testid={`project-card-reanalyze-btn-${item.id}`}
          className={styles.menuItem}
          onClick={(e) => {
            e.stopPropagation()
            setMoreBtnOpen(false)
            context?.handleRetry(item)
          }}
        >
          <AIcon type='icon-line-global_refresh'></AIcon>
          <span>{t('dashboard.projectPage.projectCard.reanalyze')}</span>
        </span>
      ),
    },
    {
      key: 'delete',
      label: (
        <span
          data-testid={`project-card-delete-btn-${item.id}`}
          className={styles.menuItem}
          onClick={(e) => {
            e.stopPropagation()
            setMoreBtnOpen(false)
            context?.handleDelete(item)
          }}
        >
          <AIcon type='icon-line-global_delete'></AIcon>
          <span>{t('dashboard.projectPage.projectCard.delete')}</span>
        </span>
      ),
    },
  ]

  const handleCardBtnClick: React.EventHandler<React.MouseEvent<Element, MouseEvent>> = (e) => {
    e.stopPropagation()
    if (item.status === 'completed') {
      message.warning('Download is not available')
    }
  }

  const creativeTypeLabel = item.durationCategory === 'five_to_ten'
    ? t('dashboard.projectPage.splashCreativeLabel', { defaultValue: 'Splash Creative' })
    : t('dashboard.projectPage.generalCreativeLabel', { defaultValue: 'General Creative' })

  return (
    <div
      data-testid={`project-card-${item.id}`}
      className={classNames(styles.projectCard, { [styles.portrait]: isPortrait })}
      onClick={() => {
        if (mode === 'default') {
          navigate(PAGE_LINKS.projectDetail, {
            state: {
              id: item.id,
              returnTo: `${routerLocation.pathname}${routerLocation.search}`,
            },
          })
        } else {
          navigate(PAGE_LINKS.professorProjectDetail, { state: { id: item.id } })
        }
      }}
    >
      {mode === 'professor' && item.expertAnalyseStatus === 'part' && (
        <span className={classNames(styles.expertBadge, styles.part)}></span>
      )}
      {mode === 'professor' && item.expertAnalyseStatus === 'completed' && (
        <span className={classNames(styles.expertBadge, styles.completed)}>
          <AIcon type='icon-fill-global_check' />
        </span>
      )}
      <div className={styles.projectCardPicBox}>
        <div className={styles.badge}>{item.brandName}</div>
        {
          item.isFreeTrial && (
            <div className={styles.trial}>{t('modals.freeTrialModal.trial')}</div>
          )
        }
        <div className={styles.time}>
          <AIcon className={styles.timeIcon} type='icon-fill-global_play2'></AIcon>
          <span className={styles.text}>{dayjs(item.adDuration * 1000).format('m:ss')}</span>
        </div>
        <AImage
          className={styles.cardPic}
          src={item.coverPath}
          loading={prioritizeCover ? 'eager' : 'lazy'}
          fetchPriority={prioritizeCover ? 'high' : 'low'}
          decoding='async'
        />
      </div>
      <div className={styles.projectCardInfo}>
        <div className={styles.titleTagBox}>
          <div className={styles.projectCardTitleBox}>
            <span className={styles.projectCardTitle}>
              <span className={styles.projectCardTitleText}>{item.projectName}</span>
            </span>
            <Score
              score={item.status === 'completed' && item.score !== 0 ? item.score : '-'}
              meanScore={
                item.status === 'completed' && item.meanScore !== 0
                  ? item.meanScore
                  : '-'
              }
            />
          </div>
          {isPortrait && (
            <div className={styles.creativeTypeTag}>
              <AIcon type='icon-a-chuangyilinggan1' />
              <span>{creativeTypeLabel}</span>
            </div>
          )}
        </div>
        <div className={styles.projectCardStatusBox}>
          <span className={styles.timeStr}>
            {item.status === 'failed' && (
              <AIcon
                width={16}
                height={16}
                style={{ color: '#FF4500', position: 'relative', left: -3 }}
                type='icon-line-global_warning'
              />
            )}
            {item.status === 'completed' && (
              <AIcon
                width={16}
                height={16}
                style={{ color: '#00C49F', position: 'relative', left: -3 }}
                type='icon-line-global_check'
              />
            )}
            {
              item.status === 'waiting' && (
                <AIcon
                  width={16}
                  height={16}
                  style={{ color: '#FFA94A', position: 'relative', left: -3 }}
                  type='icon-line-global_Waiting' />
              )}
            {
              INTERNAL_HOST.includes(location.hostname)
                ? dayjs(item.createdAt * 1000).format('YYYY-MM-DD HH:mm')
                : dayjsService.convertUtcTimestampToTZ(item.createdAt, 'YYYY-MM-DD HH:mm')
            }
            {
              item.status === 'waiting' && (
                <Popover content={<div className={styles.errorMsg}>{t('dashboard.projectPage.message.waitingQuestion')}</div>}>
                  <span>
                    <AIcon
                      width={16}
                      height={16}
                      style={{ color: '#595959', position: 'relative', left: 2, top: 1 }}
                      type='icon-line-global_question'
                    />
                  </span>
                </Popover>
              )
            }
            {item.status === 'failed' && (
              <Popover content={<div className={styles.errorMsg}>{item.errorMsg}</div>}>
                <span>
                  <AIcon
                    width={16}
                    height={16}
                    style={{ color: '#595959', position: 'relative', left: 2, top: 1 }}
                    type='icon-line-global_question'
                  />
                </span>
              </Popover>
            )}
          </span>
          {mode === 'default' && (
            <div className={styles.actionBox}>
              <span
                className={classNames(styles.downloadBtn, {
                  [styles.disabled]: item.status !== 'completed',
                })}
                style={{ display: 'none' }}
                onClick={handleCardBtnClick}
              >
                <AIcon type='icon-line-global_download' />
              </span>
              <Dropdown
                menu={{
                  items:
                    item.status === 'failed'
                      ? menuItems
                      : menuItems.filter((a) => a.key !== 'reanalyze'),
                }}
                placement='bottomLeft'
                dropdownRender={ProjectCardDropdownRender}
                open={moreBtnOpen}
                onOpenChange={(open) => setMoreBtnOpen(open)}
              >
                <span className={styles.more} onClick={(e) => e.stopPropagation()} data-testid={`project-card-more-btn-${item.id}`}>
                  <AIcon width={16} height={16} type='icon-fill-global_more' />
                </span>
              </Dropdown>
            </div>
          )}
        </div>
      </div>
      {(item.status === 'creating' || item.status === 'analyzing') &&
        progressData?.[item.id] !== undefined && (
          <div style={{ width: '100%', position: 'absolute', bottom: 0, left: 0, zIndex: 1 }}>
            <ProgressLine percent={progressData?.[item.id].process / 100} />
          </div>
        )}
    </div>
  )
}

function ProjectCardDropdownRender(menus: React.ReactNode): React.ReactNode {
  return <div className={styles.projectCardDropdownRender}>{menus}</div>
}

function Score({ score, meanScore }: { score: number | '-'; meanScore: number | '-' }) {
  const lower = typeof score === 'number' && typeof meanScore === 'number' && score < meanScore
  return (
    <div className={styles.score}>
      {lower ? (
        <img
          className={styles.scoreBg}
          src={commonUtils.getImagePath('projectPage/project-card-score-lower.png')}
          alt=''
        />
      ) : (
        <img
          className={styles.scoreBg}
          src={commonUtils.getImagePath('projectPage/project-card-score-high.png')}
          alt=''
        />
      )}
      <span className={classNames(styles.scoreText, { [styles.lower]: lower })}>{score}</span>
    </div>
  )
}

export default ProjectCard
